import { action, makeObservable, observable } from "mobx";
import RootStore from "store/rootStore";
import { PreviewResult } from "pages/import/components/LabelAutocomplete";

export type ImportLabelsStep = 1 | 2;
export type ImportMode = "COPY" | "LINK";
export type ProjectOption = { value: string; label: string; client?: string };

class ImportLabelsStore {
  @observable step: ImportLabelsStep = 1;
  @observable labels: string[] = [];
  @observable projects: ProjectOption[] = [];
  @observable currentProjectId: string | null = null;
  @observable previewResult: PreviewResult | null = null;
  @observable previewLoading: boolean = false;
  @observable batchPrefix: string = "";
  @observable batchCount: string = "";
  @observable nbImagesPerBatch: string = "";
  @observable importMode: ImportMode = "COPY";
  @observable generating: boolean = false;
  @observable operationTrackerId: string | null = null;
  @observable generateError: string | null = null;

  constructor(private store: RootStore) {
    makeObservable(this);
  }

  @action initForProject = (projectId: string) => {
    if (this.currentProjectId !== null && this.currentProjectId !== projectId) {
      this.reset();
    }
    this.currentProjectId = projectId;
  };

  @action setStep = (step: ImportLabelsStep) => {
    this.step = step;
  };

  @action setLabels = (labels: string[]) => {
    this.labels = labels;
  };

  @action setProjects = (projects: ProjectOption[]) => {
    this.projects = projects;
  };

  @action setPreviewResult = (result: PreviewResult | null) => {
    this.previewResult = result;
  };

  @action setPreviewLoading = (loading: boolean) => {
    this.previewLoading = loading;
  };

  @action setImportMode = (mode: ImportMode) => {
    this.importMode = mode;
  };

  @action setBatchPrefix = (value: string) => {
    this.batchPrefix = value;
  };

  @action setBatchCount = (value: string) => {
    this.batchCount = value;
    const total = this.previewResult?.total;
    if (total && value !== "") {
      const count = parseInt(value, 10);
      if (count > 0) {
        this.nbImagesPerBatch = String(Math.ceil(total / count));
      }
    } else {
      this.nbImagesPerBatch = "";
    }
  };

  @action setNbImagesPerBatch = (value: string) => {
    this.nbImagesPerBatch = value;
    const total = this.previewResult?.total;
    if (total && value !== "") {
      const size = parseInt(value, 10);
      if (size > 0) {
        this.batchCount = String(Math.ceil(total / size));
      }
    } else {
      this.batchCount = "";
    }
  };

  @action generate = async (
    appFetch: (url: string, opts?: any) => Promise<any>,
    projectId: string,
    clientId: string,
    labelingType: string,
  ) => {
    this.generating = true;
    this.generateError = null;
    this.operationTrackerId = null;
    try {
      const result = await appFetch("/api/projects/from-existing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          clientId,
          labelingType,
          sourceProjectIds: this.projects.map((p) => p.value),
          labelFilter: this.labels,
          importMode: this.importMode,
          nbImagesPerBatch: parseInt(this.nbImagesPerBatch, 10),
          prefix: this.batchPrefix,
          deadline: null,
          userIds: [],
        }),
      });
      this.operationTrackerId = result?.operationTrackerId ?? null;
    } catch (e) {
      this.generateError = (e as any)?.message ?? "Une erreur est survenue";
    } finally {
      this.generating = false;
    }
  };

  @action reset = () => {
    this.step = 1;
    this.labels = [];
    this.projects = [];
    this.currentProjectId = null;
    this.previewResult = null;
    this.previewLoading = false;
    this.batchPrefix = "";
    this.batchCount = "";
    this.nbImagesPerBatch = "";
    this.importMode = "COPY";
    this.generating = false;
    this.operationTrackerId = null;
    this.generateError = null;
  };
}

export default ImportLabelsStore;
